import { formatMMSS } from '@/lib/utils/formatTime';

/**
 * One resend-code cooldown shared by every flow (withdraw, API key, password,
 * 2FA, auth pages): a code sent anywhere locks resending everywhere.
 */
export const RESEND_COOLDOWN_STORAGE_KEY = 'resend_available_at';
/** Fired in the current tab on every change; other tabs get `storage`. */
export const RESEND_COOLDOWN_EVENT = 'resend-cooldown-change';
/** Used until the backend reports `retryAfter`. */
export const RESEND_COOLDOWN_SECONDS = 60;
/** Guards against a bogus `retryAfter` (e.g. ms) or junk in storage locking sends for hours. */
const MAX_COOLDOWN_SECONDS = 3600;

/** Record that a code was just sent; resend unlocks in `seconds`. */
export function markResendCooldown(seconds = RESEND_COOLDOWN_SECONDS): void {
  try {
    localStorage.setItem(RESEND_COOLDOWN_STORAGE_KEY, String(Date.now() + seconds * 1000));
    window.dispatchEvent(new Event(RESEND_COOLDOWN_EVENT));
  } catch {}
}

export function readResendCooldown(): number {
  try {
    const availableAt = Number(localStorage.getItem(RESEND_COOLDOWN_STORAGE_KEY));
    if (!availableAt) return 0;
    const seconds = Math.ceil((availableAt - Date.now()) / 1000);
    return Math.min(Math.max(0, seconds), MAX_COOLDOWN_SECONDS);
  } catch {
    return 0;
  }
}

export function clearResendCooldown(): void {
  try {
    localStorage.removeItem(RESEND_COOLDOWN_STORAGE_KEY);
    window.dispatchEvent(new Event(RESEND_COOLDOWN_EVENT));
  } catch {}
}

/** Button label with the remaining cooldown, e.g. "Confirm (00:42)". */
export function formatCooldownLabel(label: string, secondsLeft: number): string {
  return secondsLeft > 0 ? `${label} (${formatMMSS(secondsLeft)})` : label;
}

export function isRateLimited(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 429;
}

/**
 * Seconds until resend is allowed, from `retryAfter` on a response payload or
 * on an API error body. The backend doesn't send it yet — callers fall back
 * to `RESEND_COOLDOWN_SECONDS`.
 */
export function extractRetryAfter(source: unknown): number | undefined {
  const { retryAfter, body } = (source ?? {}) as { retryAfter?: unknown; body?: unknown };
  const seconds = Number(retryAfter ?? parseBody(body)?.retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : undefined;
}

function parseBody(body: unknown): { retryAfter?: unknown } | undefined {
  if (typeof body !== 'string') return body as { retryAfter?: unknown } | undefined;
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
}
